import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { MutableRefObject, ReactNode } from "react";
import type { ProposalDecision, ProposalVersion } from "@/features/catalog_case_review/shared/model/agentProposal";
import type { FieldActionCompose } from "@/features/catalog_case_review/shared/model/fieldActionContext";
import type { CaseReviewAgentBindings } from "../model/caseReviewAgentBindings";
import type { WhoDrugReviewBinding } from "../model/whoDrugReview";
import type { WorkQueueAgentBindings } from "../model/workQueueAgentBindings";

/** The part of a screen's registration the dock renders, so it is state and re-renders the dock. */
export interface AgentCapabilitySnapshot {
  /** Every proposal's decision, by proposal key, so the card can show what became of each. */
  proposalDecisions: Record<string, ProposalDecision>;
  /** Where each pending proposal stands in its history, by proposal key, so the card can offer Undo. */
  proposalVersions: Record<string, ProposalVersion>;
}

export const NO_SNAPSHOT: AgentCapabilitySnapshot = { proposalDecisions: {}, proposalVersions: {} };

/** The field whose button started the question Theragenie is working on, so that button can say so. */
export interface WorkingField {
  fieldId: string;
  recordId: string | null;
}

/** What the current screen lets the agent do in the browser. */
export interface AgentCapabilities {
  /** The Case Review's bindings while that screen is mounted, read at dispatch time; null elsewhere. */
  caseReview: () => CaseReviewAgentBindings | null;
  /** The work queue's bindings while that screen is mounted, read at dispatch time; null elsewhere. */
  workQueue: () => WorkQueueAgentBindings | null;
  snapshot: AgentCapabilitySnapshot;
  /** The dock's field-action composer while the dock is mounted and available; null elsewhere. */
  composeFieldAction: FieldActionCompose | null;
  /** The field a running field action belongs to; null when Theragenie is idle or answering a typed question. */
  workingField: WorkingField | null;
}

export const NO_CAPABILITIES: AgentCapabilities = {
  caseReview: () => null, workQueue: () => null, snapshot: NO_SNAPSHOT, composeFieldAction: null, workingField: null,
};

interface Registry {
  whoDrug: MutableRefObject<WhoDrugReviewBinding | null>;
  dockHost: HTMLElement | null;
  setDockHost: (host: HTMLElement | null) => void;
  bindings: MutableRefObject<CaseReviewAgentBindings | null>;
  workQueue: MutableRefObject<WorkQueueAgentBindings | null>;
  snapshot: AgentCapabilitySnapshot;
  publishSnapshot: (snapshot: AgentCapabilitySnapshot) => void;
  composer: FieldActionCompose | null;
  publishComposer: (composer: FieldActionCompose | null) => void;
  workingField: WorkingField | null;
  publishWorkingField: (field: WorkingField | null) => void;
}

const AgentCapabilityContext = createContext<Registry | undefined>(undefined);

/**
 * Carries the Case Review's executors to the dock, which is mounted in the layout and cannot see
 * the routed page's state any other way.
 *
 * <p>The bindings are functions over the page's live state, so they are held in a ref and refreshed
 * every render without a state change — publishing them as state would re-render the page that
 * publishes them, which would publish again. Only the serialisable snapshot is state.
 */
export function AgentCapabilityProvider({ children }: { children: ReactNode }) {
  const whoDrug = useRef<WhoDrugReviewBinding | null>(null);
  const [dockHost, setDockHost] = useState<HTMLElement | null>(null);
  const bindings = useRef<CaseReviewAgentBindings | null>(null);
  const workQueue = useRef<WorkQueueAgentBindings | null>(null);
  const [snapshot, setSnapshot] = useState<AgentCapabilitySnapshot>(NO_SNAPSHOT);
  // Held in state (not a ref) so a row's button can enable itself the moment the dock registers.
  const [composer, setComposer] = useState<FieldActionCompose | null>(null);
  // Wrapped: a function handed to a state setter is otherwise taken for an updater and called.
  const publishComposer = useCallback((next: FieldActionCompose | null) => setComposer(() => next), []);
  const [workingField, setWorkingField] = useState<WorkingField | null>(null);
  const value = useMemo<Registry>(() => ({ whoDrug, dockHost, setDockHost, bindings, workQueue, snapshot, publishSnapshot: setSnapshot, composer, publishComposer,
    workingField, publishWorkingField: setWorkingField }), [snapshot, composer, publishComposer, workingField, dockHost]);
  return <AgentCapabilityContext.Provider value={value}>{children}</AgentCapabilityContext.Provider>;
}

/**
 * Register this screen's bindings while it is mounted. A no-op outside a provider, like the
 * location: a screen rendered on its own in a test should render, not crash.
 */
export function usePublishAgentCapabilities(bindings: CaseReviewAgentBindings, snapshot: AgentCapabilitySnapshot): void {
  const registry = useContext(AgentCapabilityContext);
  // The ref and the setter are stable; the registry object is not (it carries the snapshot), and
  // depending on it would publish, re-render, and publish again without end.
  const bindingsRef = registry?.bindings;
  const publish = registry?.publishSnapshot;
  useEffect(() => {
    if (bindingsRef === undefined) return;
    bindingsRef.current = bindings;
    return () => { bindingsRef.current = null; };
  });
  // Serialized, so a page that rebuilds an equal snapshot on every render does not re-publish.
  const serialized = JSON.stringify(snapshot);
  useEffect(() => {
    if (publish === undefined) return;
    publish(JSON.parse(serialized) as AgentCapabilitySnapshot);
  }, [publish, serialized]);
  useEffect(() => () => publish?.(NO_SNAPSHOT), [publish]);
}

/**
 * Register the work queue's bindings while it is mounted: the same ref discipline as the Case
 * Review's, and the same no-op outside a provider.
 */
export function usePublishWorkQueueCapabilities(bindings: WorkQueueAgentBindings): void {
  const registry = useContext(AgentCapabilityContext);
  const ref = registry?.workQueue;
  useEffect(() => {
    if (ref === undefined) return;
    ref.current = bindings;
    return () => { ref.current = null; };
  });
}

/**
 * Register the dock's field-action composer while the dock is mounted. The other direction of the
 * same registry: the page reads it to enable the field buttons. A no-op outside a provider.
 */
export function usePublishFieldActionComposer(composer: FieldActionCompose | null): void {
  const registry = useContext(AgentCapabilityContext);
  const publish = registry?.publishComposer;
  useEffect(() => {
    if (publish === undefined) return;
    publish(composer);
    return () => publish(null);
  }, [publish, composer]);
}

/**
 * The setter the conversation uses to mark which field's action is running. A no-op outside a
 * provider, so a surface rendered on its own in a test still runs.
 */
export function useWorkingFieldPublisher(): (field: WorkingField | null) => void {
  const registry = useContext(AgentCapabilityContext);
  const publish = registry?.publishWorkingField;
  return useCallback((field: WorkingField | null) => publish?.(field), [publish]);
}

/** The registered capabilities, or none outside a provider. */
export function useAgentCapabilities(): AgentCapabilities {
  const registry = useContext(AgentCapabilityContext);
  return useMemo<AgentCapabilities>(() => registry === undefined ? NO_CAPABILITIES
    : { caseReview: () => registry.bindings.current, workQueue: () => registry.workQueue.current, snapshot: registry.snapshot,
        composeFieldAction: registry.composer, workingField: registry.workingField }, [registry]);
}

/** The shared dock can live inside a review workspace without replacing its conversation. */
export function useWhoDrugWorkspace() {
  const registry = useContext(AgentCapabilityContext);
  return { host: registry?.dockHost ?? null, setHost: registry?.setDockHost,
    binding: registry?.whoDrug };
}

export function usePublishWhoDrugReview(binding: WhoDrugReviewBinding | null) {
  const { binding: ref } = useWhoDrugWorkspace();
  useEffect(() => {
    if (!ref) return;
    ref.current = binding;
    return () => { ref.current = null; };
  });
}
